import Vue from 'vue';
import type { Route } from 'vue-router';
import Router from 'vue-router';
import { getGlobalHandle } from '@getaccept/lib-shared/src/helpers';
import type { EidType } from './signing/types/enums/eid-type';
import type { CriiptoStatus } from './signing/types/enums/criipto-status';
import { getIdOptions } from './signing/constants/eid-options';
import type { Id } from './signing/types/eid';
import { RootAction } from './store/root.types';
import store from './store/root.store';
import { sessionNamespace } from './session/store/session.constants';
import { SessionAction } from './session/store/session.types';
import type { BankIdSigningType, BankIdStep } from './signing/types/enums/bankid-se';
import { SigningHelper } from './signing/helpers/signing.helper';
import { SigningAction, signingNamespace } from './signing/store/signing.constants';

Vue.use(Router);

const loadDsrPublished = () =>
  import(/* webpackChunkName: "dsr-published" */ '@getaccept/dsr-published-site').then(
    ({ DsrPublished }) => DsrPublished
  );
const loadSigningPage = () => import(/* webpackChunkName: "signing-page" */ './pages/Signing.vue');

const router = new Router({
  mode: 'history',
  base: getGlobalHandle()._basePath,
  routes: [
    {
      path: '/v/preview/:documentId',
      meta: { title: 'Signing', signingType: 'v3', preview: true },
      component: loadSigningPage,
    },
    {
      path: '/v/:documentId/:recipientId/a/:authId/video/:videoId?',
      meta: { title: 'Signing', signingType: 'v3', urlType: 'v' },
      component: loadSigningPage,
    },
    {
      path: '/v/:documentId/:recipientId/a/:authId/',
      meta: { title: 'Signing', signingType: 'v3', urlType: 'v' },
      component: loadSigningPage,
    },
    {
      path: '/share/d/:universalId/',
      meta: { title: 'Signing', universal: true },
      component: loadSigningPage,
    },
    {
      path: '/dsr/:documentId',
      component: loadDsrPublished,
    },
    {
      path: '/dsr/:documentId/:recipientId/a/:authId/',
      component: loadDsrPublished,
    },
  ],
});

router.beforeEach(async (to: Route, _from: Route, next) => {
  const { recipientId, documentId, userId, authId, videoId }: any = to.params;
  const { token, orderRef, step, signingType, autoStartToken, eidStatus, eidType } = to.query;
  const { preview } = to.meta;

  store.dispatch(RootAction.SetIds, {
    documentId,
    recipientId,
    authId,
    userId,
    videoReminderId: videoId,
  });
  const hasBankIdParams: boolean = SigningHelper.hasValidBankIdParams(
    orderRef as string,
    step as BankIdStep,
    signingType as BankIdSigningType,
    autoStartToken as string
  );
  const hasCriiptoParams: boolean = SigningHelper.hasValidCriiptoParams(
    eidType as EidType,
    eidStatus as CriiptoStatus
  );
  if (hasBankIdParams) {
    store.dispatch(`${signingNamespace}/${SigningAction.SetBankIdQueryParams}`, {
      orderRef,
      step,
      signingType,
      autoStartToken,
    });
  } else if (hasCriiptoParams) {
    const selectedEid = getIdOptions().find((value: Id) => value.key === (eidType as EidType));
    store.dispatch(
      `${signingNamespace}/${SigningAction.SetCriiptoQueryParams}`,
      {
        selectedEid,
        status: eidStatus,
      },
      { root: true }
    );
  }
  if (preview) {
    store.dispatch(RootAction.SetPreviewMode, true);
    if (token) {
      store.dispatch(`${sessionNamespace}/${SessionAction.SetPreviewToken}`, token, {
        root: true,
      });
    }
  } else if (token && !preview) {
    store.dispatch(`${sessionNamespace}/${SessionAction.LoadTokenSuccess}`, {
      token: { jwt: token },
    });
  }
  if (token || hasBankIdParams) {
    return router.push({
      path: to.path,
      query: {
        pageId: to.query.pageId || undefined,
      },
    });
  }

  return next();
});

export default router;
